import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, dashboardPathFor } from '../lib/auth';
import { apiError } from '../lib/api';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setBusy(true);
    try {
      const u = await login(email, password);
      // Always route to the newly-authenticated user's own dashboard.
      // Ignore `loc.state.from` because it may point to a route the previous
      // user had access to that the new user does not.
      nav(dashboardPathFor(u.role), { replace: true });
    } catch (e) { setErr(apiError(e, 'Login failed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="container my-5" style={{maxWidth: 480}} data-testid="login-page">
      <h1>Welcome back</h1>
      <p className="text-muted-2">Log in to manage your business, enquiries and dashboards.</p>
      <div className="ap-card mt-3">
        {err && <div className="alert alert-danger" data-testid="login-error">{err}</div>}
        <form onSubmit={submit} data-testid="login-form">
          <label className="form-label">Email</label>
          <input className="form-control mb-3" type="email" required value={email} onChange={e=>setEmail(e.target.value)} data-testid="login-email" />

          <label className="form-label">Password</label>
          <div style={{ position: 'relative' }}>
            <input
              className="form-control"
              type={showPassword ? 'text' : 'password'}
              required
              value={password}
              onChange={e=>setPassword(e.target.value)}
              style={{ paddingRight: 42 }}
              data-testid="login-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              title={showPassword ? 'Hide password' : 'Show password'}
              style={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                border: 'none',
                background: 'transparent',
                padding: 6,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: showPassword ? '#4338CA' : '#94a3b8',
                borderRadius: 6,
              }}
              data-testid="login-toggle-password"
            >
              {showPassword ? (
                // Eye-off icon (hide)
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.584 10.587a2 2 0 0 0 2.828 2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.363 5.365A9.466 9.466 0 0 1 12 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 0 1-4.132 5.411M6.228 6.228A10.012 10.012 0 0 0 2.457 12c1.274 4.057 5.065 7 9.543 7a9.44 9.44 0 0 0 4.293-1.03" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                // Eye icon (show)
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M2.457 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.543 7-1.274 4.057-5.065 7-9.543 7-4.477 0-8.268-2.943-9.543-7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>

          <button className="btn-ap w-100 mt-3" type="submit" disabled={busy} data-testid="login-submit">
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <div className="d-flex justify-content-between mt-3 small">
          <Link to="/forgot" data-testid="login-forgot-link">Forgot password?</Link>
          <Link to="/register" data-testid="login-register-link">Create account</Link>
        </div>
      </div>
    </div>
  );
}